import React, {
  createContext,
  forwardRef,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import useScrolBarSize from '../../hooks/useScrollbarSize';
import {
  assignColumnAccessor,
  Column,
  IdArrayInput,
  ItemLoader,
} from '../../hooks/useTablePages';
import {
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from './Table';
import styled from '@emotion/styled';
import { Checkbox } from '../Form';
import useSize from '@react-hook/size';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { ColumnConfig } from '../../hooks/useColumnConfiguration';
import TableActionBar from './TableActionBar';
import { Theme } from '../../theme';
import { OrderBy } from '../../hooks/useOrderBy';
import TableColumn, { TableColumnProps } from './TableColumn';

import './TableWindow.css';
import { Box, Flex, FlexProps } from 'rebass';
import InlineNotice from '../InlineNotice/InlineNotice';
import ColumnSelector from './ColumnSelector';
import { RightPaneConfig } from '../../hooks/useRightPaneConfiguration';
import Icon, { IconType } from '../Icon/Icon';
import useRangeSelection, {
  preventShiftTextSelection,
} from '../../hooks/useRangeSelection';
import { orderPinnedColumns } from './columnPinning';

const noop = () => {};

const pinnedCellStyle = (left: number): React.CSSProperties => ({
  position: 'sticky',
  left,
  zIndex: 2,
  background: 'inherit',
});

const pinnedOffset = (
  columns: Column[],
  index: number,
  selectionWidth: number
) =>
  columns
    .slice(0, index)
    .reduce(
      (offset, column) => offset + (column.pinned ? Number(column.width) : 0),
      selectionWidth
    );

// The body owns vertical scrolling and its shadow, including native overscroll.
const ListLayout = createContext({
  width: 0,
  shadowHeight: 0,
  pinnedWidth: 0,
  showShadow: false,
});
const ListInner = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ListInner({ children, style, ...props }, ref) {
  const layout = useContext(ListLayout);
  return (
    <div
      {...props}
      ref={ref}
      style={{
        ...style,
        width: layout.width,
      }}
    >
      {layout.showShadow && layout.pinnedWidth > 0 && (
        <div
          aria-hidden="true"
          className="pinned-column-shadow"
          style={{
            width: layout.pinnedWidth,
            height: layout.shadowHeight,
            marginBottom: -layout.shadowHeight,
          }}
        />
      )}
      {children}
    </div>
  );
});

ListInner.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
};

const StyledTable = styled(Table)<{
  maxWidth?: number;
  maxHeight?: number;
  theme: Theme;
}>`
  box-sizing: border-box;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  position: relative;

  .table-header {
    position: relative;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.tableHeader};
    z-index: 3;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  }

  .table-header-viewport {
    overflow: hidden;
  }

  .table-header > .pinned-column-shadow {
    position: absolute;
  }

  .tbody {
    white-space: nowrap;
    /* The header is a separate viewport: keep horizontal endpoints aligned. */
    overscroll-behavior-x: none;
  }

  /* Share the cells' native scroll/overscroll layer, without taking row space. */
  .pinned-column-shadow {
    position: sticky;
    left: 0;
    top: 0;
    z-index: 4;
    pointer-events: none;
  }

  .pinned-column-shadow::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 0;
    bottom: 0;
    width: 6px;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.22), transparent);
  }

  .table-header-row {
    position: sticky;
    top: 0;
    overflow: visible;
    z-index: 3;
    border-top-right-radius: 0;
  }

  .pinned-cell {
    align-self: stretch;
  }

  .checkbox-cell {
    display: flex;
    align-items: center;
  }

  input[type='checkbox'] {
    margin: 0;
    padding: 0;
  }

  ${({ maxHeight }) => (maxHeight ? `max-height: ${maxHeight}px` : '')};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px` : '')};
`;

const StyledTableRow = styled(TableRow)`
  position: absolute;
  width: 100%;
  overflow: visible;
`;

const StyledCheckbox = styled(Checkbox)<{ color?: string }>`
  position: relative;
  margin-top: -2px;
  margin-bottom: -3px;
  margin-left: -2px;
  width: 16px;
  height: 16px;
  ${({ color }) => (color ? `color: ${color} !important` : '')}
`;

type HeaderRowProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = {
  headerHeight: number;
  totalColumnsWidth: number;
  scrollBarSize: number;
  allowSelection: boolean;
  selectAllClick?: () => void;
  selectedIds?: IdArrayInput<IdValue>;
  itemIds: IdArrayInput<IdValue>;
  columns: Column[];
  onHeaderClick?: (column: Column) => void;
  orderBy?: OrderBy[];
  checkboxColumnWidth: number;
  columnConfiguration?: ColumnConfig[];
  setColumnConfiguration?: (columns: ColumnConfig[]) => void;
};

const HeaderRow: React.FC<HeaderRowProps> = <
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
>(
  props: HeaderRowProps<Item, IdField, IdValue>
) => {
  const {
    headerHeight,
    totalColumnsWidth,
    scrollBarSize,
    allowSelection,
    selectAllClick,
    selectedIds,
    itemIds,
    columns,
    onHeaderClick,
    checkboxColumnWidth,
    orderBy,
    columnConfiguration,
    setColumnConfiguration,
  } = props;

  const dragEnabled = !!setColumnConfiguration && !!columnConfiguration;

  const reorder = (
    listOfColumns: ColumnConfig[],
    startIndex: number,
    endIndex: number
  ): ColumnConfig[] => {
    const result = Array.from(listOfColumns);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result as ColumnConfig[];
  };

  const onDragEnd = (result: DropResult) => {
    if (
      !setColumnConfiguration ||
      !columnConfiguration ||
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const enabledColumns = columnConfiguration.filter(
      (column) => column.enabled
    );

    const sourceColumn = enabledColumns[result.source.index];
    const destinationColumn = enabledColumns[result.destination.index];
    if (!!sourceColumn.pinned !== !!destinationColumn.pinned) {
      return;
    }

    const hiddenColumns = columnConfiguration.filter(
      (column) => !column.enabled
    );

    const hiddenIndices = hiddenColumns.map((hiddenColumn) => {
      return columnConfiguration.findIndex(
        (column) => column.id === hiddenColumn.id
      );
    });

    const reorderedColumns = reorder(
      enabledColumns,
      result.source.index,
      result.destination.index
    );
    hiddenIndices.forEach((hiddenIdx, idx) =>
      reorderedColumns.splice(hiddenIdx, 0, hiddenColumns[idx])
    );
    setColumnConfiguration(reorderedColumns);
  };

  if (itemIds.length === 0) {
    return (
      <TableHeaderRow
        style={{
          height: headerHeight,
          width: totalColumnsWidth + scrollBarSize,
        }}
        className="table-header-row"
        role="row"
      />
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="visibleColumns"
        isDropDisabled={!dragEnabled}
        direction="horizontal"
      >
        {(provided, snapshot) => (
          <TableHeaderRow
            style={{
              height: headerHeight,
              width: totalColumnsWidth + scrollBarSize,
            }}
            className="table-header-row"
            role="row"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {allowSelection && (
              <TableHeaderCell
                className="checkbox-cell pinned-cell"
                role="columnheader"
                onClick={selectAllClick}
                style={{
                  width: checkboxColumnWidth,
                  ...pinnedCellStyle(0),
                }}
              >
                <StyledCheckbox
                  color="white"
                  checked={itemIds.length === selectedIds?.length}
                  onChange={noop}
                  title={
                    itemIds.length === selectedIds?.length
                      ? 'Deselect All'
                      : 'Select All'
                  }
                />
              </TableHeaderCell>
            )}
            {columns.map((column, index) => {
              const id = column.id || (column.accessor as 'string');
              const columnOrderBy = orderBy
                ? orderBy.find((value) => value.col === id)
                : undefined;

              return (
                <Draggable
                  key={id ?? index}
                  isDragDisabled={!dragEnabled || !!column.pinned}
                  draggableId={id}
                  index={index}
                >
                  {(provided, snapshot) => {
                    return (
                      <TableHeaderCell
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          width: column.width,
                          ...(column.pinned
                            ? pinnedCellStyle(
                                pinnedOffset(
                                  columns,
                                  index,
                                  allowSelection ? checkboxColumnWidth : 0
                                )
                              )
                            : undefined),
                        }}
                        className={column.pinned ? 'pinned-cell' : undefined}
                        role="columnheader"
                        onClick={() =>
                          column.sortable !== false && onHeaderClick
                            ? onHeaderClick(column)
                            : undefined
                        }
                      >
                        {column.header}
                        {columnOrderBy ? (
                          columnOrderBy.dir === 'ASC' ? (
                            <Icon icon="sort-asc" size="1.3em" />
                          ) : (
                            <Icon icon="sort-desc" size="1.3em" />
                          )
                        ) : (
                          ''
                        )}
                      </TableHeaderCell>
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </TableHeaderRow>
        )}
      </Droppable>
    </DragDropContext>
  );
};

HeaderRow.propTypes = {
  headerHeight: PropTypes.any,
  totalColumnsWidth: PropTypes.any,
  allowSelection: PropTypes.any,
  selectAllClick: PropTypes.any,
  selectedIds: PropTypes.any,
  itemIds: PropTypes.any,
  columns: PropTypes.any,
  onHeaderClick: PropTypes.any,
  checkboxColumnWidth: PropTypes.any,
  orderBy: PropTypes.array,
  columnConfiguration: PropTypes.any,
  setColumnConfiguration: PropTypes.any,
};

const ItemRow: React.FC<ListChildComponentProps> = ({ data, index, style }) => {
  const {
    itemIds,
    itemLoader,
    columns,
    selectedIds,
    onSelectRow,
    rowHeight,
    allowSelection,
    checkboxColumnWidth,
    onRowClick,
    activeIds,
  } = data;
  const item = itemLoader.useItem(index);
  const itemId = itemIds[index];
  const selected = selectedIds ? selectedIds.indexOf(itemId) !== -1 : false;
  const onClick = useCallback(
    (e) => {
      if (
        onRowClick &&
        !(e as any).target.matches(
          'button, .checkbox-cell, .checkbox-cell *, input, a, button *, a *'
        )
      ) {
        onRowClick(item, index);
      }
    },
    [onRowClick, item]
  );
  const selectRowClick = useCallback(
    (e) => {
      onSelectRow(index, e.shiftKey);
    },
    [onSelectRow, index]
  );

  return (
    <StyledTableRow
      key={'item-' + itemId}
      style={{ top: style.top, height: rowHeight }}
      role="row"
      altRow={index % 2 === 1}
      onClick={onClick}
      className={activeIds && activeIds.includes(itemId) ? 'active' : undefined}
    >
      {allowSelection && (
        <TableCell
          key="checkbox"
          className="checkbox-cell pinned-cell"
          role="cell"
          style={{
            width: checkboxColumnWidth,
            ...pinnedCellStyle(0),
          }}
          onClick={selectRowClick}
          onMouseDown={preventShiftTextSelection}
        >
          <StyledCheckbox
            checked={selected}
            onChange={noop}
            title={selected ? 'Deselect' : 'Select'}
          />
        </TableCell>
      )}
      {item &&
        columns.map((column: Column, columnIndex: number) => (
          <TableColumn
            key={column.id}
            accessor={column.accessor as TableColumnProps['accessor']}
            width={column.width}
            item={item}
            className={column.pinned ? 'pinned-cell' : undefined}
            style={
              column.pinned
                ? pinnedCellStyle(
                    pinnedOffset(
                      columns,
                      columnIndex,
                      allowSelection ? checkboxColumnWidth : 0
                    )
                  )
                : undefined
            }
          />
        ))}
    </StyledTableRow>
  );
};

ItemRow.propTypes = {
  data: PropTypes.any,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
};

export type TableWindowProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = FlexProps & {
  columns: Column[];
  columnConfiguration?: ColumnConfig[];
  setColumnConfiguration?: (columns: ColumnConfig[]) => void;
  itemIds: IdArrayInput<IdValue>;
  itemLoader: ItemLoader<Item, IdField, IdValue>;
  selectedIds?: IdArrayInput<IdValue>;
  activeIds?: IdArrayInput<IdValue>;
  onSelectionChange?: (selectedIds: IdArrayInput<IdValue>) => void;
  onHeaderClick?: (column: Column) => void;
  onRowClick?: (item: Item, index: number) => void;
  orderBy?: OrderBy[];
  actionItems?: React.ReactNode;
  showActionBar?: boolean;
  showHeader?: boolean;
  rowHeight?: number;
  fillWidth?: boolean;
  csvFilename?: string;
  rightPane?: JSX.Element;
  rightPaneConfig?: RightPaneConfig;
  setRightPaneConfig?: (value: RightPaneConfig | null) => void;
  rightPaneIcon?: IconType;
  emptyMessage?: React.ReactNode;
};

/**
 *
 * @component TableWindow that displays virtualized list of rows
 * Assumes that it always has a checkbox selection as the first column
 */
const TableWindow: React.FC<TableWindowProps> = <
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
>({
  columns,
  itemIds,
  itemLoader,
  selectedIds,
  activeIds,
  onSelectionChange,
  onHeaderClick,
  onRowClick,
  orderBy,
  columnConfiguration,
  setColumnConfiguration,
  actionItems,
  showActionBar = true,
  showHeader = true,
  rowHeight = 32,
  fillWidth = false,
  csvFilename,
  rightPane,
  rightPaneConfig,
  setRightPaneConfig,
  rightPaneIcon,
  emptyMessage,
  ...props
}: TableWindowProps<Item, IdField, IdValue>) => {
  useEffect(() => {
    columns.forEach(assignColumnAccessor);
  }, [columns]);

  const orderedColumns = useMemo(() => orderPinnedColumns(columns), [columns]);

  const scrollBarSize = useScrolBarSize();

  const allowSelection =
    !!onSelectionChange || (!!selectedIds && selectedIds.length > 0);

  const headerHeight = showHeader ? 36 : 0;
  const actionBarHeight = showActionBar && itemIds.length > 0 ? 48 : 0;
  const checkboxColumnWidth = 40;
  const borderWidth = 1;

  const baseColumnsWidth: number = useMemo(
    () =>
      orderedColumns.reduce<number>(
        (previousValue, currentValue) =>
          previousValue + (currentValue.width as number),
        allowSelection ? checkboxColumnWidth : 0
      ),
    [orderedColumns, allowSelection, checkboxColumnWidth]
  );
  const tableRef = useRef<HTMLDivElement>(null);
  const [width] = useSize(tableRef);

  const fillColumns = orderedColumns.filter(
    (column) => column.fillRemainingWidth
  );
  const availableColumnsWidth = Math.max(
    0,
    width - borderWidth * 2 - scrollBarSize
  );
  const extraColumnsWidth =
    fillWidth && fillColumns.length > 0
      ? Math.max(0, availableColumnsWidth - baseColumnsWidth)
      : 0;
  const effectiveColumns = useMemo(
    () =>
      extraColumnsWidth > 0
        ? orderedColumns.map((column) =>
            column.fillRemainingWidth && typeof column.width === 'number'
              ? {
                  ...column,
                  width: column.width + extraColumnsWidth / fillColumns.length,
                }
              : column
          )
        : orderedColumns,
    [orderedColumns, extraColumnsWidth, fillColumns.length]
  );
  const totalColumnsWidth = baseColumnsWidth + extraColumnsWidth;

  const selectAllClick = useCallback(() => {
    if (onSelectionChange) {
      const allSelected = itemIds.length === selectedIds?.length;
      onSelectionChange(allSelected ? [] : itemIds);
    }
  }, [onSelectionChange, itemIds, selectedIds]);

  const onSelectRow = useRangeSelection(
    itemIds,
    selectedIds,
    onSelectionChange
  );

  const hasHorizontalScroll = totalColumnsWidth > availableColumnsWidth;
  const [horizontallyScrolled, setHorizontallyScrolled] = useState(false);
  const pinnedWidth = pinnedOffset(
    effectiveColumns,
    effectiveColumns.length,
    allowSelection ? checkboxColumnWidth : 0
  );

  const [listRendered, setListRendered] = useState<boolean>(false);
  const onItemsRendered = useCallback(() => {
    setListRendered(true);
  }, []);
  const scrollListRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (headerScrollRef.current && scrollListRef.current) {
      headerScrollRef.current.scrollLeft = scrollListRef.current.scrollLeft;
    }
  }, [totalColumnsWidth, width]);
  useEffect(() => {
    const activeId = activeIds?.[0];
    if (activeId && scrollListRef.current && listRendered) {
      const activeIndex = itemIds.indexOf(activeId);
      if (activeIndex === -1) {
        return;
      }
      const listElement = scrollListRef.current;
      const elementPosition = activeIndex * rowHeight;
      const listHeight = Math.max(listElement.offsetHeight, 300);
      const isAboveFold = elementPosition < listElement.scrollTop;
      const isBelowFold =
        elementPosition + rowHeight >= listElement.scrollTop + listHeight;
      if (isAboveFold) {
        listElement.scrollTop = Math.max(elementPosition, 0);
      } else if (isBelowFold) {
        listElement.scrollTop = Math.max(
          elementPosition - listHeight + rowHeight,
          0
        );
      }
    }
  }, [activeIds?.[0], scrollListRef.current, listRendered]);

  const hasRightPane =
    rightPane && rightPaneConfig?.visible && itemIds.length > 0;
  const maxTableWidth = totalColumnsWidth + scrollBarSize + borderWidth * 2;
  const maxTableHeight =
    itemIds.length * rowHeight +
    headerHeight +
    borderWidth * 2 +
    actionBarHeight +
    (hasHorizontalScroll ? scrollBarSize : 0);

  if (itemIds.length === 0 && emptyMessage) {
    return <InlineNotice alignSelf="flex-start">{emptyMessage}</InlineNotice>;
  }

  return (
    <Flex
      flexDirection="column"
      flexShrink={1}
      flexGrow={1}
      sx={{
        maxHeight: maxTableHeight,
      }}
      {...(props as any)}
    >
      <Flex
        flexDirection="row"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
        }}
      >
        <Flex
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            position: 'relative',
            zIndex: 5,
            width: '100%',
            '.table, .table-header-row': {
              borderTopRightRadius: hasRightPane ? 0 : undefined,
            },
            maxWidth: fillWidth ? undefined : maxTableWidth,
          }}
        >
          <StyledTable
            ref={tableRef}
            onScrollCapture={(event) => {
              const body = scrollListRef.current;
              const header = headerScrollRef.current;
              if (body && header) {
                if (event.target === header) {
                  body.scrollLeft = header.scrollLeft;
                }
                if (event.target === body || event.target === header) {
                  header.scrollLeft = body.scrollLeft;
                }
              }
              if (event.target === scrollListRef.current) {
                setHorizontallyScrolled(scrollListRef.current.scrollLeft > 0);
              }
            }}
            className="table"
            style={{
              borderBottomLeftRadius:
                showActionBar && itemIds.length > 0 ? 0 : undefined,
              borderBottomRightRadius:
                showActionBar && itemIds.length > 0 ? 0 : undefined,
              borderBottom: showActionBar && itemIds.length > 0 ? 0 : undefined,
            }}
            role="table"
          >
            <AutoSizer>
              {({ height, width }) => (
                <ListLayout.Provider
                  value={{
                    width: Math.max(totalColumnsWidth, width - scrollBarSize),
                    pinnedWidth,
                    showShadow: horizontallyScrolled && itemIds.length > 0,
                    shadowHeight: Math.min(
                      Math.max(
                        0,
                        height -
                          headerHeight -
                          (hasHorizontalScroll ? scrollBarSize : 0)
                      ),
                      itemIds.length * rowHeight
                    ),
                  }}
                >
                  {showHeader && (
                    <div
                      className="table-header"
                      style={{ height: headerHeight, width }}
                    >
                      <div
                        className="table-header-viewport"
                        ref={headerScrollRef}
                        style={{ height: headerHeight, width }}
                      >
                        <HeaderRow
                          headerHeight={headerHeight}
                          totalColumnsWidth={Math.max(
                            totalColumnsWidth,
                            width - scrollBarSize
                          )}
                          scrollBarSize={scrollBarSize}
                          allowSelection={allowSelection}
                          selectAllClick={selectAllClick}
                          selectedIds={selectedIds}
                          itemIds={itemIds}
                          columns={effectiveColumns}
                          onHeaderClick={onHeaderClick}
                          checkboxColumnWidth={checkboxColumnWidth}
                          orderBy={orderBy}
                          columnConfiguration={columnConfiguration}
                          setColumnConfiguration={setColumnConfiguration}
                        />
                      </div>
                      {horizontallyScrolled &&
                        pinnedWidth > 0 &&
                        itemIds.length > 0 && (
                          <div
                            aria-hidden="true"
                            className="pinned-column-shadow"
                            style={{ width: pinnedWidth, height: headerHeight }}
                          />
                        )}
                    </div>
                  )}
                  <FixedSizeList
                    innerElementType={ListInner}
                    outerRef={scrollListRef}
                    onItemsRendered={onItemsRendered}
                    overscanCount={3}
                    className="tbody"
                    height={Math.max(0, height - headerHeight)}
                    width={width}
                    itemCount={itemIds.length}
                    itemSize={rowHeight}
                    itemKey={(index, data) => data.itemIds[index]}
                    itemData={{
                      itemIds,
                      itemLoader,
                      columns: effectiveColumns,
                      selectedIds,
                      activeIds,
                      onRowClick,
                      onSelectRow,
                      rowHeight,
                      allowSelection,
                      checkboxColumnWidth,
                    }}
                  >
                    {ItemRow}
                  </FixedSizeList>
                </ListLayout.Provider>
              )}
            </AutoSizer>
          </StyledTable>
          {showHeader &&
            itemIds.length > 0 &&
            columnConfiguration &&
            setColumnConfiguration &&
            columnConfiguration.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 5,
                  top: '5px',
                  right: '4px',
                }}
              >
                <ColumnSelector
                  columnConfiguration={columnConfiguration}
                  setColumnConfiguration={setColumnConfiguration}
                />
              </Box>
            )}
        </Flex>
        {hasRightPane && (
          <Flex
            sx={{
              boxSizing: 'border-box',
              borderTopRightRadius: 'default',
              borderWidth: hasRightPane ? '1px' : 0,
              borderStyle: 'solid',
              borderColor: 'tableBorder',
              flexGrow: 1,
              flexShrink: 1,
              width: '100%',
              // transition: 'all .3s ease-in-out',
              overflow: 'hidden',
              maxWidth: hasRightPane ? rightPaneConfig.width : '0px',
            }}
          >
            {rightPane}
          </Flex>
        )}
      </Flex>
      {showActionBar && itemIds.length > 0 && (
        <TableActionBar
          p={2}
          className="table-actions"
          bg="tableActions"
          sx={{
            // transition: 'all .3s ease-in-out',
            backfaceVisibility: 'hidden',
            borderBottomRightRadius: 'default',
            borderBottomLeftRadius: 'default',
            maxWidth: hasRightPane
              ? maxTableWidth + (rightPaneConfig?.width ?? 0)
              : maxTableWidth,
          }}
          columns={orderedColumns}
          selectedIds={selectedIds}
          numItems={itemIds.length}
          itemLoader={itemLoader}
          rightPaneConfig={rightPaneConfig}
          setRightPaneConfig={setRightPaneConfig}
          rightPaneIcon={rightPaneIcon}
          csvFilename={csvFilename ?? `${itemLoader.queryName}.csv`}
        >
          {itemIds.length > 0 && actionItems}
        </TableActionBar>
      )}
    </Flex>
  );
};

export default TableWindow;
